import React, { FC, useEffect } from "react";
import { CheckCircleFilled } from "@ant-design/icons";
import {
  Button,
  Col,
  Form,
  FormInstance,
  Input,
  Modal,
  Row,
  Select,
  Typography,
  Divider,
} from "antd";

const { Title, Text } = Typography;

const PRIMARY = "rgb(0, 76, 143)";
const ACCENT = "#722ed1";

const authenticationTypeOptions = [
  { label: "Mobile OTP", value: "MOBILE_OTP" },
];

export type DischargeCghsAuthFormValues = {
  authenticationType: string;
  mobileNumber: string;
  otp: string;
};

type Props = {
  open: boolean;
  onCancel: () => void;
  onSubmit: (values: DischargeCghsAuthFormValues) => void | Promise<void>;
  confirmLoading?: boolean;
  authForm: FormInstance;
  otpVerificationForm: FormInstance;
  handleOTPVerification: () => void | Promise<void>;
  handleMobileVerification: () => void | Promise<void>;
  handleResendOtp?: () => void | Promise<void>;
  isMobileNumberVerified?: boolean;
  isOTPVerified?: boolean;
  isOTPVerificationLoading?: boolean;
  patientResponse?: any;
  patientName?: string | null;
};

const DischargeCghsNewModal: FC<Props> = ({
  open,
  onCancel,
  onSubmit,
  confirmLoading,
  authForm,
  otpVerificationForm,
  handleOTPVerification,
  handleMobileVerification,
  handleResendOtp,
  isMobileNumberVerified,
  isOTPVerified,
  isOTPVerificationLoading,
  patientResponse,
  patientName,
}) => {
  // ✅ Watch OTP value
  const otpValue = Form.useWatch("otp", otpVerificationForm);

  useEffect(() => {
    if (open && patientResponse?.primaryMobile) {
      otpVerificationForm.setFieldsValue({
        mobile: patientResponse.primaryMobile,
      });
    }
  }, [open, patientResponse?.primaryMobile]);

  const handleFinalSubmit = async () => {
    if (!isOTPVerified) return;

    const authValues = await authForm.validateFields();
    const otpValues = await otpVerificationForm.validateFields();

    await onSubmit({
      authenticationType: authValues.authenticationType,
      mobileNumber: otpValues.mobile,
      otp: otpValues.otp,
    });
  };

  // ✅ Condition for Verify OTP button
  const isVerifyOtpDisabled =
    !isMobileNumberVerified || isOTPVerified || !otpValue;

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={1000}
      centered
      destroyOnClose
    >
      {(patientName ?? patientResponse?.fullName) && (
        <div
          style={{
            marginBottom: 16,
            padding: "12px 16px",
            background: "#f5f8ff",
            borderRadius: 10,
            border: "1px solid #e6f0ff",
          }}
        >
          <Text
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "#555",
              marginRight: 6,
            }}
          >
            Verify Patient:
          </Text>

          <Text
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: PRIMARY,
            }}
          >
            {patientName ?? patientResponse?.fullName}
          </Text>
        </div>
      )}

      <Divider style={{ margin: "12px 0" }} />

      <Row gutter={[16, 16]}>
        <Col span={8}>
          <Form
            form={authForm}
            layout="vertical"
            initialValues={{ authenticationType: "MOBILE_OTP" }}
          >
            <Form.Item
              label="Authentication Type"
              name="authenticationType"
              rules={[{ required: true }]}
            >
              <Select options={authenticationTypeOptions} size="large" />
            </Form.Item>
          </Form>
        </Col>

        <Col span={16}>
          <Form form={otpVerificationForm} layout="vertical">
            <Row gutter={12}>
              <Col span={10}>
                <Form.Item
                  label="Mobile Number"
                  name="mobile"
                  rules={[
                    { required: true },
                    {
                      pattern: /^\d{10}$/,
                      message: "Enter valid 10-digit mobile",
                    },
                  ]}
                >
                  <Input
                    addonBefore="+91"
                    size="large"
                    maxLength={10} 
                    disabled={isOTPVerified}
                    suffix={
                      isMobileNumberVerified ? (
                        <CheckCircleFilled style={{ color: "#52c41a" }} />
                      ) : (
                        <Button
                          type="link"
                          size="small"
                          onClick={handleMobileVerification}
                          style={{ padding: 0 }}
                        >
                          Verify
                        </Button>
                      )
                    }
                  />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item label="OTP" name="otp">
                  <Input
                    size="large"
                    maxLength={6} 
                    disabled={!isMobileNumberVerified || isOTPVerified}
                    suffix={
                      handleResendOtp &&
                      isMobileNumberVerified &&
                      !isOTPVerified ? (
                        <Button
                          type="link"
                          size="small"
                          onClick={handleResendOtp}
                          style={{ padding: 0 }}
                        >
                          Resend
                        </Button>
                      ) : null
                    }
                  />
                </Form.Item>
              </Col>

              <Col
                span={6}
                style={{ display: "flex", alignItems: "center", marginTop: 8 }}
              >
                <Button
                  block
                  type="primary"
                  size="large"
                  loading={isOTPVerificationLoading}
                  disabled={isVerifyOtpDisabled}
                  style={{
                    background: isVerifyOtpDisabled
                      ? "#d9d9d9"
                      : PRIMARY,
                    borderColor: isVerifyOtpDisabled
                      ? "#d9d9d9"
                      : PRIMARY,
                    color: isVerifyOtpDisabled ? "#999" : "#fff",
                  }}
                  onClick={handleOTPVerification}
                >
                  Verify OTP
                </Button>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>

      <Divider />

      {/* Footer */}
      <div
        style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}
      >
        <Button
          type="primary"
          size="large"
          loading={confirmLoading}
          disabled={!isOTPVerified}
          style={{
            background: isOTPVerified ? PRIMARY : "#d9d9d9",
            borderColor: isOTPVerified ? PRIMARY : "#d9d9d9",
            color: isOTPVerified ? "#fff" : "#999",
            cursor: isOTPVerified ? "pointer" : "not-allowed",
          }}
          onClick={handleFinalSubmit}
        >
          Discharge
        </Button>
      </div>
    </Modal>
  );
};

export default DischargeCghsNewModal;
